import { Button } from '@hyperflake/react-ui-library';
import { InfoCircleIcon } from '@icon/index';
import { CircularProgress } from '@library/loaders/components';
import React from 'react';

type Props = {
    title?: string;

    // pick mode
    isPicking: boolean;
    onTogglePick: () => void;

    // inputs
    latitude: string;
    longitude: string;
    zoom: string;
    onLatitudeChange: (value: string) => void;
    onLongitudeChange: (value: string) => void;
    onZoomChange: (value: string) => void;

    // 🔥 add these
    onZoomFocus?: () => void;
    onZoomBlur?: () => void;

    // unsaved
    hasUnsavedChanges: boolean;

    // actions
    onReset: () => void;
    onSave: () => void;
    isSubmitting?: boolean;
};

const ClientMapClientViewControlsPanel = ({
    title = 'Map View',

    isPicking,
    onTogglePick,

    latitude,
    longitude,
    zoom,
    onLatitudeChange,
    onLongitudeChange,
    onZoomChange,
    onZoomFocus,
    onZoomBlur,

    hasUnsavedChanges,

    onReset,
    onSave,
    isSubmitting = false,
}: Props) => {
    return (
        <div className="absolute top-6 right-6 z-10 bg-white rounded-lg shadow-lg p-6 w-80 max-h-[calc(100%-3rem)] overflow-y-auto mt-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            </div>

            {/* Pick Location */}
            <div className="mb-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>

                {isPicking && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-xs text-blue-800 flex items-start gap-2">
                            <InfoCircleIcon />
                            <span>Click on the map to pick a new location.</span>
                        </p>
                    </div>
                )}

                <div className="flex justify-between gap-2">
                    <div className="mb-3 w-1/2">
                        <label className="block text-xs text-gray-600 mb-1">Latitude</label>
                        <input
                            type="number"
                            step="any"
                            value={latitude}
                            onChange={(e) => onLatitudeChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 25.276987"
                        />
                    </div>

                    <div className="mb-3 w-1/2">
                        <label className="block text-xs text-gray-600 mb-1">Longitude</label>
                        <input
                            type="number"
                            step="any"
                            value={longitude}
                            onChange={(e) => onLongitudeChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 55.296249"
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="block text-xs text-gray-600 mb-1">Zoom</label>
                    <input
                        type="number"
                        step="0.01"
                        value={zoom}
                        onChange={(e) => onZoomChange(e.target.value)}
                        onFocus={onZoomFocus}
                        onBlur={onZoomBlur}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Tip: scroll on map also updates this.</p>
                </div>

                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onTogglePick}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                            isPicking
                                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {isPicking ? 'Picking…' : 'Pick Location'}
                    </button>

                    <button
                        type="button"
                        onClick={onReset}
                        className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md text-sm font-medium transition-colors"
                    >
                        Reset
                    </button>
                </div>
            </div>

            {/* Unsaved */}
            {hasUnsavedChanges && (
                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-xs text-yellow-800 flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                        Unsaved changes
                    </p>
                </div>
            )}

            <Button type="button" size="lg" className="w-full" onClick={onSave} disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={16} color="inherit" /> : 'Save'}
            </Button>
        </div>
    );
};

export default ClientMapClientViewControlsPanel;
